import { useCallback, useRef } from 'react';
import type React from 'react';
import { toastError } from '../../../utils/toast';
import i18n from '../../../i18n';
import { planCellKey } from '../../../utils/planCells';
import { isBedEntity, isPlotEntity } from '../../../utils/planEntities';
import type { PlanAppMode } from '../../../types/planManagement';
import type { PlanningSelectionInfo } from '../../../types/planManagement';
import type { EntityLookup } from '../../../utils/planEntities';
import type { BulkPlacementPreview } from './types';

const PAINT_DEBOUNCE_MS = 250;

interface UseGridMouseHandlersParams {
  appMode: PlanAppMode;
  allowBedSelection: boolean;
  isBusy: boolean;
  isDragging: boolean;
  isEraseMode: boolean;
  isBulkPlacementMode: boolean;
  bulkPlacementPreview: BulkPlacementPreview | null;
  entityLookup: EntityLookup;
  onPatch: (ops: Array<{ op: 'paintCells'; cells: Array<{ row: number; col: number; kind?: any }> }>) => Promise<void>;
  onPlotClick?: (plotId: string) => void;
  onEmptyCellClick?: (row: number, col: number) => void;
  handleConfirmBulkPlacement: () => void;
  clearEntitySelection: () => void;
  onHoverCell: (row: number, col: number) => void;
  onCellClick: (row: number, col: number) => void;
  setPlanningSelection: (selection: PlanningSelectionInfo | null) => void;
  setSelectionStart: (value: { row: number; col: number } | null) => void;
  setSelectionEnd: (value: { row: number; col: number } | null) => void;
  setIsDragging: (value: boolean) => void;
  setMultiSelectedBeds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setMultiSelectedPlots: React.Dispatch<React.SetStateAction<Set<string>>>;
  setSelectedBedForPlanting: (id: string | null) => void;
  setPlantingModalVisible: (open: boolean) => void;
  setBulkPlacementHoverPos: (pos: { row: number; col: number } | null) => void;
  onManagementPlotAction?: (plotIds: string[]) => void;
  onManagementBedClick?: (bedId: string) => void;
}

export const useGridMouseHandlers = (params: UseGridMouseHandlersParams) => {
  const paintQueueRef = useRef<Map<string, { row: number; col: number; kind?: any }>>(new Map());
  const paintTimeoutRef = useRef<number | null>(null);

  const queuePaint = useCallback((row: number, col: number, kind?: any) => {
    const key = planCellKey(row, col);
    paintQueueRef.current.set(key, { row, col, kind });
    if (!paintTimeoutRef.current) {
      paintTimeoutRef.current = window.setTimeout(async () => {
        const ops = Array.from(paintQueueRef.current.values());
        paintQueueRef.current.clear();
        paintTimeoutRef.current = null;
        if (ops.length > 0) await params.onPatch([{ op: 'paintCells', cells: ops }]);
      }, PAINT_DEBOUNCE_MS);
    }
  }, [params]);

  const handleMouseDown = useCallback((row: number, col: number, e?: React.MouseEvent) => {
    if (params.isBusy) return;
    if (params.isBulkPlacementMode) {
      if (!params.bulkPlacementPreview?.isValid) return toastError(i18n.t('planning:gridMouse.positionUnavailable'));
      return params.handleConfirmBulkPlacement();
    }

    const key = planCellKey(row, col);
    const entity = params.entityLookup.get(key);

    if (params.appMode === 'planning' && params.allowBedSelection) {
      params.setPlanningSelection(null);
      params.setIsDragging(true);
      params.setSelectionStart({ row, col });
      params.setSelectionEnd({ row, col });
      return;
    }
    if (params.appMode === 'planning' && !params.allowBedSelection) {
      if (entity && isPlotEntity(entity) && params.onPlotClick) return params.onPlotClick(entity.plotId);
      if (!entity && params.onEmptyCellClick) return params.onEmptyCellClick(row, col);
      return params.onCellClick(row, col);
    }
    if (params.appMode === 'planting') {
      if (params.isEraseMode) {
        params.setIsDragging(true);
        return queuePaint(row, col, 'empty');
      }
      if (entity && isBedEntity(entity)) {
        if (e?.ctrlKey || e?.metaKey) {
          params.setMultiSelectedBeds((prev) => {
            const next = new Set(prev);
            if (next.has(entity.bedId)) next.delete(entity.bedId);
            else next.add(entity.bedId);
            return next;
          });
        } else {
          params.setMultiSelectedBeds(new Set());
          params.setSelectedBedForPlanting(entity.bedId);
          params.setPlantingModalVisible(true);
        }
      }
      return;
    }

    if (params.appMode === 'management') {
      if (entity && isBedEntity(entity)) {
        if (e?.ctrlKey || e?.metaKey) {
          params.setMultiSelectedBeds((prev) => {
            const next = new Set(prev);
            if (next.has(entity.bedId)) next.delete(entity.bedId);
            else next.add(entity.bedId);
            return next;
          });
          params.clearEntitySelection();
        } else {
          params.setMultiSelectedBeds(new Set());
          if (params.onManagementBedClick) {
            params.onManagementBedClick(entity.bedId);
          } else {
            params.onCellClick(row, col);
          }
        }
      } else if (entity && isPlotEntity(entity)) {
        if (e?.ctrlKey || e?.metaKey) {
          params.setMultiSelectedPlots((prev) => {
            const next = new Set(prev);
            if (next.has(entity.plotId)) next.delete(entity.plotId);
            else next.add(entity.plotId);
            return next;
          });
          params.clearEntitySelection();
        } else if (params.onManagementPlotAction) {
          params.setMultiSelectedBeds(new Set());
          params.setMultiSelectedPlots(new Set());
          params.onManagementPlotAction([entity.plotId]);
        } else if (params.onPlotClick) {
          params.onPlotClick(entity.plotId);
        }
      } else {
        params.clearEntitySelection();
        params.setMultiSelectedBeds(new Set());
        params.setMultiSelectedPlots(new Set());
      }
    }
  }, [params, queuePaint]);

  const handleMouseEnter = useCallback((row: number, col: number) => {
    if (params.isBulkPlacementMode) return params.setBulkPlacementHoverPos({ row, col });
    if (params.appMode === 'planning' || params.appMode === 'management' || params.appMode === 'planting') params.onHoverCell(row, col);
    if (!params.isDragging || params.isBusy) return;
    if (params.appMode === 'planning' && params.allowBedSelection) return params.setSelectionEnd({ row, col });
    if (params.appMode === 'planting' && params.isEraseMode) queuePaint(row, col, 'empty');
  }, [params, queuePaint]);

  return { handleMouseDown, handleMouseEnter, queuePaint };
};
